// ---------------------------------------------------------------------------
// Telephone-encounter reason presets
//
// CIFC (the practice) maintains a fixed list of telephone-encounter "reasons"
// that map to ECW report categories. The registrar picks one when creating a
// telephone encounter, or just types a free-text reason — a typed reason that
// doesn't match a preset is stored with the `OTHER` code (see below).
//
// There is no synced catalog for these in the EHR cache today, so they live
// here as a hardcoded company list. When a per-company reason catalog is
// synced, `getReasonPresets` in `telephoneEncounterService` can read it and
// fall back to this list.
// ---------------------------------------------------------------------------

import type {ReasonPreset} from '../../types/telephoneEncounter';

// The sentinel code for a free-text reason. When the registrar types a reason
// that isn't one of the presets, it's stored under this code with the typed
// text as the reason, so downstream knows it's custom rather than a coded
// category. It is not a selectable row in the picker.
export const OTHER_REASON_CODE = 'OTHER';

// Stable codes are derived from the label so they survive label tweaks; keep
// them in sync if a label's identity (not just wording) changes.
export const TELEPHONE_ENCOUNTER_REASONS: ReasonPreset[] = [
  {code: 'APPT_REQUEST', label: 'Appt Request'},
  {code: 'HISTORICAL_DATA', label: 'Historical Data'},
  {code: 'COVID_RESULT', label: 'COVID Result'},
  {code: 'FEVER', label: 'Fever'},
  {code: 'CALLBACK_FROM_OFFICE', label: 'Needs call back from office'},
  {code: 'NEEDS_INFO_FAXED', label: 'Needs info faxed'},
  {code: 'REFILLS', label: 'Refills'},
  {code: 'RX_FAXED', label: 'Rx faxed'},
  {code: 'TIEH_GRANT', label: 'TIEH Grant'},
  {code: 'WANTS_TO_SEE_YOU', label: 'Wants to see you'},
  {code: 'WHC_ABNORMAL_PAP_TRACKING', label: 'WHC Abnormal Pap Tracking'},
  {code: 'BLOOD_WORK_RESULTS', label: 'Blood Work Results'},
  {code: 'IMAGING_RESULTS', label: 'Imaging Results'},
  {code: 'WAITING_FOR_CALLBACK', label: 'Waiting for call back'},
  {code: 'ER_DISCHARGE', label: 'ER Discharge'},
  {code: 'URGENT_VISIT_REQUEST', label: 'Urgent visit request'},
  {code: 'CALLBACK_FROM_MD', label: 'Needs call back from MD'},
  {code: 'NEEDS_REFERRAL', label: 'Needs referral'},
  {code: 'RETURNED_CALL', label: 'Returned call'},
  {code: 'DM_MANAGEMENT_VISIT', label: 'Request for DM Management visit'},
  {code: 'PCC_ASTHMA_GAPS_IN_CARE', label: 'PCC-Asthma Gaps In Care'},
  {code: 'IMMUNIZATION_RECORDS', label: 'Immunization records'},
  {code: 'MEDICAL_RECORDS_REQUEST', label: 'Medical records request'},
  {code: 'PRAPARE', label: 'PRAPARE'},
  {code: 'PATIENT_ENGAGEMENT_FU', label: 'Patient Engagement F/U'},
  {code: 'PHQ9_TIMEDOC', label: 'PHQ9 TimeDoc'},
  {code: 'POSTPARTUM_GDM', label: 'Postpartum GDM'},
  {code: 'SECOND_CALL', label: 'Second call'},
  {code: 'TEST_RESULTS', label: 'Test results'},
  {code: 'URI_TRIAGE', label: 'URI Triage'},
  {code: 'CIFC_CCM', label: 'CIFC CCM'},
  {code: 'ABDOMINAL_PAIN', label: 'Abdominal pain'},
  {code: 'DR_TOLD_PATIENT_TO_CALL', label: 'Dr. told patient to call'},
  {code: 'MESSAGE_TO_SELF', label: 'Message to self'},
  {code: 'HOSPITAL_DISCHARGE', label: 'Hospital Discharge'},
  {
    code: 'PCC_HOME_HEALTH_VNA_SNF_DISCHARGE',
    label: 'PCC-Home Health/VNA or SNF discharge',
  },
  {code: 'PCC_ED_HOSPITAL_DISCHARGE', label: 'PCC-ED/Hospital Discharge'},
  {code: 'INTERNAL_THERAPY_REFERRAL', label: 'Internal Therapy Referral'},
  {
    code: 'INTERNAL_MEDICATION_MANAGEMENT_REFERRAL',
    label: 'Internal Medication Management Referral',
  },
  {code: 'INSURE_TEST_ORDERED', label: 'INSURE Test Ordered'},
  {code: 'IM_BH_COORDINATION', label: 'IM BH Coordination'},
  {code: 'IM_OBGYN_COORDINATION', label: 'IM OBGYN Coordination'},
  {code: 'JOINT_PAIN', label: 'Joint pain'},
  {code: 'ANNUAL_WELLNESS_VISIT', label: 'Annual Wellness Visit'},
  {code: 'TRIAGE', label: 'Triage'},
  {code: 'MEDICATION_QUESTIONS', label: 'Medication questions'},
];

export default TELEPHONE_ENCOUNTER_REASONS;
